// All-sites map view for the Locations page. Centred + auto-fitted
// to the bounding box of every site with coordinates. Click a pin or
// the site list row to drill into that site's location tree.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Chip, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded'
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
// Side-effect import — extends the `L` namespace with `L.markerClusterGroup`
// (both the runtime function and, via @types/leaflet.markercluster, the
// TS declaration merge). Groups nearby pins into a single cluster bubble
// at lower zoom levels instead of rendering every marker individually —
// with hundreds of sites, that individual-DOM-node cost per marker was
// the actual "map feels slow to load" complaint, not anything about how
// the sync effect below diffs updates (that part was already correct).
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

import type { Site } from '@cmms/types'
import { formatSiteName } from '../../../lib/siteDisplayName'
import { COLOR_BY_LABELS, type ColorBy, type LegendEntry } from '../lib/siteColors'

interface MapViewProps {
  sites: Site[]
  /** Selected site id — the map flies to its pin when set, and that
   *  pin renders with the highlight style. */
  selectedSiteId?: string | null
  /** Called when a pin or the corresponding list row is clicked. */
  onSelectSite: (siteId: string) => void
  /** Optional per-site fill colour. When provided (color-by enabled)
   *  the pin uses this colour instead of the default idle teal. The
   *  selected pin keeps the same colour but renders larger + with a
   *  contrasting ring so it remains distinguishable. */
  getPinColor?: (site: Site) => string | undefined
  /** Active "Color by" dimension — drives the legend overlay header. */
  colorBy?: ColorBy
  /** Pre-computed legend rendered as an overlay in the top-right of
   *  the map. Empty / undefined → no legend shown. */
  legend?: LegendEntry[]
  /** Currently-highlighted legend value (clicking a legend chip toggles
   *  this). Sites with the matching dimension value stay vivid; others
   *  fade. `null` → no highlight, render normally. */
  highlightedLegendValue?: string | null
  /** Toggle handler for legend clicks. */
  onLegendValueClick?: (value: string | null) => void
  /** Helper to read the current dimension value of a site so the map
   *  can decide whether a pin matches the highlighted legend entry. */
  getDimensionValue?: (site: Site) => string | null
}

// Custom pin SVG — circle-on-stake shape with optional pulse + dim
// states. Colours and ring vary with mode to stay readable on either
// the light or dark basemap. CSS keyframes for the pulse are injected
// once on module load (see ensurePinStyles).
function buildPinIcon(opts: {
  color: string
  ringColor: string
  highlighted?: boolean
  pulse?: boolean
  dimmed?: boolean
}): L.DivIcon {
  ensurePinStyles()
  const { color, ringColor, highlighted = false, pulse = false, dimmed = false } = opts
  const size = highlighted ? 38 : 30
  const shadow = highlighted
    ? `0 6px 18px -4px ${color}90`
    : `0 4px 10px -3px rgba(15, 23, 42, 0.45)`
  // Subtle radial bevel on the pin head — makes it feel less flat.
  const bevel = `radial-gradient(circle at 35% 30%, ${color}ff 0%, ${color}d8 65%, ${color}c0 100%)`
  return L.divIcon({
    className: 'cmms-site-pin',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size + 6],
    html: `
      <div class="cmms-pin-root${dimmed ? ' is-dimmed' : ''}" style="
        position: relative; width: ${size}px; height: ${size}px;
        display: flex; align-items: flex-start; justify-content: center;
      ">
        ${pulse ? `
          <span class="cmms-pin-pulse" style="
            position: absolute; left: 50%; top: 50%;
            transform: translate(-50%, -50%);
            width: ${size}px; height: ${size}px;
            border-radius: 50%; background: ${color};
            opacity: 0.55; pointer-events: none;
          "></span>` : ''}
        <div class="cmms-pin-head" style="
          width: ${size}px; height: ${size}px; border-radius: 50%;
          background: ${bevel};
          border: 3px solid ${ringColor};
          box-shadow: ${shadow};
          display: flex; align-items: center; justify-content: center;
          transition: transform 200ms ease, box-shadow 200ms ease;
        ">
          <svg width="${highlighted ? 18 : 14}" height="${highlighted ? 18 : 14}"
               viewBox="0 0 24 24" fill="white">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>
      </div>
    `,
  })
}

// Cluster bubble — a scaled-up variant of the pin's own circle-and-
// ring language (same bevel/shadow treatment) rather than the plugin's
// default yellow/orange rings, so it reads as part of the same design
// system instead of a bolted-on library default.
function buildClusterIcon(count: number, isDark: boolean): L.DivIcon {
  ensurePinStyles()
  const color = isDark ? '#5EEAD4' : '#0D9488'
  const ringColor = isDark ? '#0F172A' : '#FFFFFF'
  const size = count >= 100 ? 52 : count >= 10 ? 44 : 36
  const bevel = `radial-gradient(circle at 35% 30%, ${color}ff 0%, ${color}d8 65%, ${color}c0 100%)`
  return L.divIcon({
    className: 'cmms-site-cluster',
    iconSize: [size, size],
    html: `
      <div style="
        width: ${size}px; height: ${size}px; border-radius: 50%;
        background: ${bevel};
        border: 3px solid ${ringColor};
        box-shadow: 0 4px 12px -3px rgba(15, 23, 42, 0.5);
        display: flex; align-items: center; justify-content: center;
        color: white; font-weight: 800; font-size: ${count >= 100 ? 14 : 13}px;
        font-family: inherit;
      ">${count}</div>
    `,
  })
}

// Inject the pulse + dim/hover keyframes exactly once. We avoid
// emotion/styled-components here so the marker HTML can be raw.
let _stylesInjected = false
function ensurePinStyles(): void {
  if (_stylesInjected || typeof document === 'undefined') return
  _stylesInjected = true
  const css = `
    @keyframes cmmsPinPulse {
      0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0.55; }
      70%  { transform: translate(-50%, -50%) scale(2.2); opacity: 0;    }
      100% { transform: translate(-50%, -50%) scale(2.2); opacity: 0;    }
    }
    .cmms-pin-pulse {
      animation: cmmsPinPulse 1.8s ease-out infinite;
    }
    .cmms-pin-root.is-dimmed { opacity: 0.32; filter: saturate(0.45); }
    .cmms-pin-root           { transition: opacity 220ms ease, filter 220ms ease; }
    .cmms-pin-root:hover .cmms-pin-head {
      transform: translateY(-2px) scale(1.06);
      box-shadow: 0 8px 22px -4px rgba(15, 23, 42, 0.5) !important;
    }
  `
  const tag = document.createElement('style')
  tag.dataset.cmmsPin = '1'
  tag.textContent = css
  document.head.appendChild(tag)
}

// Esri World Street Map — free, no API key required. Vibrant blue
// water, green parks, beige roads — colourful in both light and dark
// UI themes (we keep the same tiles either way so the map stays
// readable instead of going monochrome in dark mode). Previously
// CARTO's Voyager CDN, which now watermarks unauthenticated tiles.
const TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'

export function MapView({
  sites, selectedSiteId, onSelectSite, getPinColor,
  colorBy = 'none', legend = [],
  highlightedLegendValue = null, onLegendValueClick,
  getDimensionValue,
}: MapViewProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // Legend can be collapsed to a compact pill so it doesn't crowd the
  // map. Default open — keep the information density high on first
  // glance.
  const [legendOpen, setLegendOpen] = useState(true)
  // Sites-without-coordinates panel — default CLOSED. This used to be a
  // permanent wrapping chip list docked under the map, which ate a big
  // chunk of the map's height on any org with more than a handful of
  // un-geocoded sites. It's secondary information ("these exist but
  // aren't pinned"), not something the user needs open by default.
  const [noCoordOpen, setNoCoordOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef       = useRef<L.Map | null>(null)
  const tileLayerRef = useRef<L.TileLayer | null>(null)
  const clusterRef   = useRef<L.MarkerClusterGroup | null>(null)
  // Keep markers keyed by site id so we can update only the changed
  // ones on re-render (swap icon to highlighted, recenter, etc.).
  const markersRef   = useRef<Map<string, L.Marker>>(new Map())
  // `iconCreateFunction` below is set once, at cluster-group creation
  // time, so it closes over whatever `isDark` was on mount — a ref
  // keeps it reading the CURRENT theme on every cluster repaint instead
  // of a stale one. The marker-sync effect calls `refreshClusters()`
  // when `isDark` changes so existing clusters actually repaint.
  const isDarkRef = useRef(isDark)
  isDarkRef.current = isDark

  // Partition sites into "has coords" / "no coords" once per data change.
  const { coordSites, noCoordSites } = useMemo(() => {
    const c: Site[] = []
    const nc: Site[] = []
    for (const s of sites) {
      if (s.latitude != null && s.longitude != null
        && Number.isFinite(s.latitude) && Number.isFinite(s.longitude)) {
        c.push(s)
      } else {
        nc.push(s)
      }
    }
    return { coordSites: c, noCoordSites: nc }
  }, [sites])

  // ── Mount the map once ─────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: false,
      worldCopyJump: true,
      // World Street Map serves up to zoom 19 — clamping prevents users
      // from zooming further (which would 404 and show the stretched
      // parent-tile "scaled label" placeholder seen in earlier loads).
      maxZoom: 19,
    })
    // Reliability + responsiveness tuning:
    //   keepBuffer:        keep more tiles around the viewport so panning
    //                      doesn't reveal white voids before the new
    //                      tiles finish downloading.
    //   updateWhenIdle:    fetch tiles continuously while panning, not
    //                      only at the end — fixes the "drag, see white,
    //                      then tiles snap in" effect.
    //   crossOrigin:       lets the browser cache tiles properly and
    //                      keeps re-paints from hitting the network.
    const tile = L.tileLayer(TILE_URL, {
      keepBuffer: 4,
      updateWhenIdle: false,
      crossOrigin: true,
      maxZoom: 19,
    })
    tile.addTo(map)
    tileLayerRef.current = tile

    // Groups nearby pins into one bubble at lower zoom, splitting apart
    // as the user zooms in — the actual fix for slow/heavy rendering
    // with a large site count, which no amount of marker-diffing (the
    // sync effect below) touches, since that's still one real Leaflet
    // marker + DOM node created per site on first load either way.
    const cluster = L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (c) => buildClusterIcon(c.getChildCount(), isDarkRef.current),
    })
    cluster.addTo(map)
    clusterRef.current = cluster

    // World view until we have markers to fit to.
    map.setView([20, 0], 2)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      tileLayerRef.current = null
      clusterRef.current = null
      markersRef.current.clear()
    }
  }, [])

  // ── Sync markers whenever the site list changes ────────────────────
  useEffect(() => {
    const map = mapRef.current
    const cluster = clusterRef.current
    if (!map || !cluster) return

    const existing = markersRef.current
    const wantedIds = new Set(coordSites.map((s) => s.id))

    // Remove markers for sites no longer in the list. `cluster.removeLayer`,
    // not `marker.remove()` — the marker was added to the cluster group,
    // not the map directly, so removing it from the map wouldn't drop it
    // out of its cluster's child count.
    for (const [id, marker] of existing) {
      if (!wantedIds.has(id)) {
        cluster.removeLayer(marker)
        existing.delete(id)
      }
    }

    // Add / refresh markers.
    const idleColor = isDark ? '#5EEAD4' : '#1F2937'           // teal-300 in dark, slate-800 in light
    const ringColor = isDark ? '#0F172A' : '#FFFFFF'           // slate-900 in dark, white in light
    for (const site of coordSites) {
      const isSelected = site.id === selectedSiteId
      // Per-site colour wins over default idle/selected colours. When
      // color-by is on we keep the dimension colour even on the
      // selected pin so the legend mapping holds — the highlight
      // is communicated through size + ring instead.
      const dimColor = getPinColor?.(site)
      const fillColor = dimColor ?? (isSelected ? '#0D9488' : idleColor)
      // Highlight logic — when a legend value is "active", only sites
      // whose dimension value matches stay vivid; others fade.
      const matchesHighlight = highlightedLegendValue == null
        ? true
        : (getDimensionValue?.(site) ?? null) === highlightedLegendValue
      const icon = buildPinIcon({
        color: fillColor,
        ringColor,
        highlighted: isSelected,
        pulse: isSelected,
        dimmed: !matchesHighlight,
      })
      let marker = existing.get(site.id)
      if (!marker) {
        marker = L.marker(
          [site.latitude as number, site.longitude as number],
          { icon }
        )
          .bindTooltip(formatSiteName(site), { direction: 'top', offset: [0, -28] })
          .on('click', () => onSelectSite(site.id))
        cluster.addLayer(marker)
        existing.set(site.id, marker)
      } else {
        marker.setIcon(icon)
        marker.setLatLng([site.latitude as number, site.longitude as number])
      }
    }

    // iconCreateFunction closed over isDarkRef (always current), but a
    // cluster bubble already on screen only repaints when something
    // tells it to — this is that trigger for a theme toggle specifically.
    cluster.refreshClusters()
  }, [coordSites, selectedSiteId, onSelectSite, isDark, getPinColor,
      highlightedLegendValue, getDimensionValue])

  // ── Auto-fit to bounds — only when the SITE LIST changes, never
  //    when the user clicks pins or closes panels. We track which
  //    site-set we last fit for via a stable signature so re-renders
  //    triggered by selection state don't reset the user's zoom. ───
  const fittedForSitesRef = useRef<string>('')
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const sig = coordSites.map((s) => s.id).sort().join(',')
    if (sig === fittedForSitesRef.current) return
    fittedForSitesRef.current = sig
    if (coordSites.length === 0) {
      map.setView([20, 0], 2)
      return
    }
    // If the user already has a site selected when the list arrives,
    // the fly-to-selected effect below will take it from here; don't
    // pre-empt it with a fitBounds.
    if (selectedSiteId && coordSites.some((s) => s.id === selectedSiteId)) return
    const bounds = L.latLngBounds(
      coordSites.map((s) => [s.latitude as number, s.longitude as number])
    )
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13, animate: true })
  }, [coordSites, selectedSiteId])

  // ── Fly to the selected site when it changes + restore the
  //    previous view when the selection clears. We remember the
  //    centre + zoom right before the first flyTo so the user can
  //    pop back to where they were (e.g. zoomed in on a region)
  //    when they close the overlay. The saved view is only set on
  //    null → site transitions; site → site transitions keep the
  //    original "previous" so a chain of pin clicks all restores
  //    to the same starting point. ─────────────────────────────────
  const savedViewRef = useRef<{ center: L.LatLngExpression; zoom: number } | null>(null)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Deselected — fly back to the saved view (if any).
    if (!selectedSiteId) {
      const saved = savedViewRef.current
      if (saved) {
        savedViewRef.current = null
        map.flyTo(saved.center, saved.zoom, { duration: 0.7 })
      }
      return
    }

    const site = coordSites.find((s) => s.id === selectedSiteId)
    if (!site || site.latitude == null || site.longitude == null) return

    // First click into a site from the "no selection" state — remember
    // where the user was so we can pop back there on close.
    if (!savedViewRef.current) {
      savedViewRef.current = { center: map.getCenter(), zoom: map.getZoom() }
    }
    map.flyTo([site.latitude, site.longitude], 15, { duration: 0.8 })
  }, [selectedSiteId, coordSites])

  // ── Fly to fit the highlighted-legend group's bounds ───────────────
  // When the user clicks a legend entry we also re-frame the map so the
  // matching pins fill the viewport. Skip when only one match exists
  // (already centred via flyTo above-style intent).
  useEffect(() => {
    const map = mapRef.current
    if (!map || highlightedLegendValue === null || !getDimensionValue) return
    const matching = coordSites.filter((s) => getDimensionValue(s) === highlightedLegendValue)
    if (matching.length === 0) return
    if (matching.length === 1) {
      const m = matching[0]!
      map.flyTo([m.latitude as number, m.longitude as number], 13, { duration: 0.7 })
      return
    }
    const bounds = L.latLngBounds(
      matching.map((s) => [s.latitude as number, s.longitude as number])
    )
    map.flyToBounds(bounds, { padding: [60, 60], maxZoom: 13, duration: 0.7 })
  }, [highlightedLegendValue, coordSites, getDimensionValue])

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      flex: 1, minWidth: 0, minHeight: 0,
      // Prevent Leaflet's animated tile / zoom-control layers from
      // bleeding outside the pane when the user zooms aggressively.
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Map */}
      <Box sx={{ position: 'relative', flex: 1, minHeight: 320, overflow: 'hidden' }}>
        <Box
          ref={containerRef}
          sx={{
            position: 'absolute', inset: 0,
            bgcolor: isDark ? '#0F172A' : '#E5E7EB',
            // In dark mode we keep CARTO Voyager's data (water = blue,
            // roads, labels) but render the raster tiles dark via a
            // CSS filter. Inverting and rotating hue 180° flips light
            // → dark while preserving the colour identity of features
            // (blue stays roughly blue). The filter targets only
            // .leaflet-tile so markers, popups, and zoom controls stay
            // unaffected.
            ...(isDark && {
              '& .leaflet-tile': {
                filter: 'invert(1) hue-rotate(180deg) brightness(0.92) saturate(1.1)',
              },
              // Smooth the seam between filtered tiles when Leaflet
              // shows a low-zoom parent underneath a loading child.
              '& .leaflet-tile-container': {
                background: '#0F172A',
              },
            }),
          }}
        />

        {/* Legend overlay — top-right of the map. Collapsible: opens
            from a compact pill into a full list with smooth motion.
            Entries are clickable: clicking highlights only those pins,
            dims the rest, and flies the map to fit the group's bounds. */}
        {colorBy !== 'none' && legend.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 12, right: 12, zIndex: 500,
            display: 'flex', flexDirection: 'column',
            maxHeight: 'calc(100% - 24px)',
            pointerEvents: 'auto',
          }}>
            <Box
              component={motion.div}
              layout
              initial={{ opacity: 0, x: 14, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 360, damping: 28 }}
              sx={{
                display: 'flex', flexDirection: 'column',
                // Fully opaque — the user reported transparency made it
                // unreadable against the map. Strong shadow + accent
                // border lift it cleanly off the basemap.
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                borderRadius: 2.5,
                boxShadow: (t) => t.palette.mode === 'light'
                  ? `0 18px 44px -12px rgba(15, 23, 42, 0.35), 0 4px 10px -4px rgba(15, 23, 42, 0.15)`
                  : `0 18px 44px -12px rgba(0, 0, 0, 0.65), 0 4px 10px -4px rgba(0, 0, 0, 0.5)`,
                overflow: 'hidden',
                minWidth: legendOpen ? 240 : 0,
                maxWidth: 300,
              }}
            >
              {/* Header — clicking it toggles open/closed. Tiny "reset"
                  button on the right when a highlight is active. */}
              <Stack
                direction="row" alignItems="center" spacing={0.85}
                onClick={() => setLegendOpen((v) => !v)}
                sx={{
                  px: 1.25, py: 1, cursor: 'pointer',
                  // Stronger gradient backdrop so the header reads as a
                  // distinct "title bar" rather than a flat strip.
                  background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.16)} 0%, ${alpha(t.palette.primary.main, 0.06)} 100%)`,
                  borderBottom: legendOpen ? '1px solid' : 'none',
                  borderColor: (t) => alpha(t.palette.primary.main, 0.15),
                  '&:hover': {
                    background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.22)} 0%, ${alpha(t.palette.primary.main, 0.1)} 100%)`,
                  },
                }}
              >
                <Box
                  component={motion.div}
                  whileHover={{ scale: 1.05 }}
                  sx={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                    color: 'primary.contrastText',
                    boxShadow: (t) => `0 4px 10px -2px ${alpha(t.palette.primary.main, 0.45)}`,
                  }}
                >
                  <PaletteOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
                    textTransform: 'uppercase', color: 'text.disabled',
                    lineHeight: 1.1,
                  }}>
                    Color by
                  </Typography>
                  <Typography sx={{
                    fontSize: 12.5, fontWeight: 700, lineHeight: 1.25,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {COLOR_BY_LABELS[colorBy]}
                    <Box component="span" sx={{
                      ml: 0.75, fontSize: 11, color: 'text.disabled', fontWeight: 600,
                    }}>
                      · {legend.length}
                    </Box>
                  </Typography>
                </Box>
                {highlightedLegendValue !== null && (
                  <Tooltip title="Reset filter" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onLegendValueClick?.(null) }}
                      sx={{
                        width: 22, height: 22,
                        color: 'primary.main',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        '&:hover': {
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.22),
                        },
                      }}
                    >
                      <RestartAltRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {legendOpen
                  ? <ExpandLessRoundedIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
                  : <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />}
              </Stack>

              {/* Body — animates open/closed with height + opacity. */}
              <AnimatePresence initial={false}>
                {legendOpen && (
                  <Box
                    component={motion.div}
                    key="legend-body"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                    sx={{ overflow: 'hidden' }}
                  >
                    <Box sx={{
                      overflowY: 'auto', p: 0.5,
                      maxHeight: 'min(360px, calc(100vh - 220px))',
                    }}>
                      {legend.map((entry, i) => {
                        const active = highlightedLegendValue === entry.value
                        const dimmed = highlightedLegendValue !== null && !active
                        return (
                          <Stack
                            component={motion.div}
                            layout
                            whileHover={{ x: 3 }}
                            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                            key={`${entry.value ?? '__none'}-${i}`}
                            direction="row" alignItems="center" spacing={1}
                            onClick={() => onLegendValueClick?.(active ? null : entry.value)}
                            sx={{
                              px: 1, py: 0.65, borderRadius: 1.5,
                              cursor: onLegendValueClick ? 'pointer' : 'default',
                              bgcolor: active
                                ? (t) => alpha(entry.color, t.palette.mode === 'dark' ? 0.22 : 0.14)
                                : 'transparent',
                              border: '1px solid',
                              borderColor: active ? alpha(entry.color, 0.55) : 'transparent',
                              opacity: dimmed ? 0.5 : 1,
                              transition: 'opacity 180ms ease, background-color 180ms ease, border-color 180ms ease',
                              '&:hover': onLegendValueClick && !active ? {
                                bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                              } : undefined,
                            }}
                          >
                            <Box
                              component={motion.div}
                              animate={{ scale: active ? 1.18 : 1 }}
                              transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                              sx={{
                                width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                                bgcolor: entry.color,
                                border: '1.5px solid',
                                borderColor: (t) => alpha(t.palette.background.paper, 0.9),
                                boxShadow: active
                                  ? `0 0 0 3px ${alpha(entry.color, 0.4)}, 0 2px 6px ${alpha(entry.color, 0.5)}`
                                  : `0 1px 3px ${alpha(entry.color, 0.5)}`,
                                transition: 'box-shadow 180ms ease',
                              }}
                            />
                            <Typography sx={{
                              flex: 1, fontSize: 11.5,
                              fontWeight: active ? 700 : 600,
                              lineHeight: 1.35,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              color: entry.value
                                ? (active ? entry.color : 'text.primary')
                                : 'text.disabled',
                              fontStyle: entry.value ? 'normal' : 'italic',
                              transition: 'color 180ms ease',
                            }}>
                              {entry.value ?? 'Unspecified'}
                            </Typography>
                            <Box sx={{
                              fontSize: 10.5, fontWeight: 800, color: active ? 'primary.contrastText' : 'text.disabled',
                              minWidth: 20, textAlign: 'center',
                              bgcolor: active ? entry.color : (t) => alpha(t.palette.text.primary, 0.06),
                              px: 0.7, py: 0.15, borderRadius: 0.75,
                              transition: 'background-color 180ms ease, color 180ms ease',
                            }}>
                              {entry.count}
                            </Box>
                          </Stack>
                        )
                      })}
                    </Box>

                    {/* Footer hint — gentle teach for first-time users */}
                    {onLegendValueClick && (
                      <Box sx={{
                        px: 1.25, py: 0.65,
                        borderTop: '1px solid', borderColor: 'divider',
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
                      }}>
                        <Typography sx={{
                          fontSize: 10, color: 'text.disabled',
                          textAlign: 'center', fontStyle: 'italic',
                        }}>
                          Click a row to zoom & isolate that group
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </AnimatePresence>
            </Box>
          </Box>
        )}
        {coordSites.length === 0 && (
          <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 500,
          }}>
            <Box sx={{
              pointerEvents: 'auto',
              bgcolor: 'background.paper',
              border: '1px solid', borderColor: 'divider',
              borderRadius: 2, px: 3, py: 2.5,
              textAlign: 'center', maxWidth: 360,
              boxShadow: (t) => `0 12px 28px -8px ${alpha(t.palette.text.primary, 0.18)}`,
            }}>
              <PublicOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                No sites on the map yet
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.55 }}>
                Add latitude + longitude to your sites in Settings → Sites
                to drop pins here. Sites without coordinates are still
                listed in the pill below.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Sites without coordinates — surfaced so they're not silently
            dropped, but as a floating collapsible pill (bottom-right,
            clear of the top-left zoom control and the top-right legend)
            instead of a permanent block docked under the map. That old
            layout ate a big chunk of the map's height on any org with
            more than a handful of un-geocoded sites — collapsed by
            default here so the map keeps the space. */}
        {noCoordSites.length > 0 && (
          <Box sx={{
            position: 'absolute', bottom: 12, right: 12, zIndex: 500,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
            maxWidth: 'calc(100% - 24px)',
            pointerEvents: 'auto',
          }}>
            <AnimatePresence initial={false}>
              {noCoordOpen && (
                <Box
                  component={motion.div}
                  key="no-coord-body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                  sx={{
                    overflow: 'hidden', order: -1, mb: 0.75,
                    bgcolor: 'background.paper',
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: 2.5,
                    boxShadow: (t) => t.palette.mode === 'light'
                      ? `0 18px 44px -12px rgba(15, 23, 42, 0.35), 0 4px 10px -4px rgba(15, 23, 42, 0.15)`
                      : `0 18px 44px -12px rgba(0, 0, 0, 0.65), 0 4px 10px -4px rgba(0, 0, 0, 0.5)`,
                  }}
                >
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{
                    p: 1, maxWidth: 320, maxHeight: 220, overflowY: 'auto',
                  }}>
                    {noCoordSites.map((s) => (
                      <Tooltip key={s.id} title="Click to open. Add latitude/longitude in Settings → Sites to pin it on the map." arrow>
                        <Chip
                          size="small"
                          icon={<LocationOnRoundedIcon sx={{ fontSize: 14 }} />}
                          label={formatSiteName(s)}
                          onClick={() => onSelectSite(s.id)}
                          sx={{
                            height: 26, fontSize: 11.5, fontWeight: 600,
                            cursor: 'pointer',
                            bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFA' : 'rgba(255,255,255,0.04)',
                            border: '1px solid', borderColor: 'divider',
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
            </AnimatePresence>

            <Stack
              component={motion.div}
              layout
              direction="row" alignItems="center" spacing={0.75}
              onClick={() => setNoCoordOpen((v) => !v)}
              sx={{
                px: 1.25, py: 0.75, cursor: 'pointer',
                bgcolor: 'background.paper',
                border: '1px solid', borderColor: 'divider',
                borderRadius: 5,
                boxShadow: (t) => t.palette.mode === 'light'
                  ? `0 8px 20px -8px rgba(15, 23, 42, 0.3)`
                  : `0 8px 20px -8px rgba(0, 0, 0, 0.55)`,
                '&:hover': { borderColor: 'text.disabled' },
              }}
            >
              <LocationOffOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3, color: 'text.secondary' }}>
                {noCoordSites.length} without coordinates
              </Typography>
              {noCoordOpen
                ? <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                : <ExpandLessRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  )
}
